const getterEditorModePPT = (state) => {
  return state.isEnableAddinPowerpoint && state.isPPTEditMode
}

// AHA-43274: "this add-in frame is NOT the frame the slideshow is currently on."
//
// A PowerPoint deck boots every add-in frame at once, each an independent presenter instance with
// its own store, so during a slideshow every background frame still holds a fully live slide. That
// slide keeps doing three things nobody asked it to: it runs the quiz countdown (which both starts
// the question and, at times-up, ends it — writing quizStatus to the server), it plays audio, and it
// re-asserts itself as the active slide on visibilitychange. This getter is the single term all
// three switch on.
//
// An earlier cut used the same condition to unmount the Middle subtree outright. That worked but
// disturbed the layout, so the frame now renders normally and only its side effects are suppressed.
//
// All four terms are load-bearing:
//   - isEnableAddinPowerpoint — no other host runs several frames of the same presentation at once,
//     so nowhere else can a frame be "not the one presenting".
//   - the flag — off restores the original behaviour exactly, everywhere this is consulted.
//   - addinPPTActiveView === 'read' — pptSlideLostFocus is a LATCH consumed by a re-assert, so a
//     frame that stands down and never regains the show carries it true back out of the slideshow.
//     Without this term that frame would sit in the EDITOR with its timers and audio disabled and
//     nothing left running to clear the flag.
//   - pptSlideLostFocus — the tag poll's verdict that the slide on screen is not this frame's.
//
// Root getter rather than a per-component computed because the consumers are spread across a Vuex
// module (audio) and three components that share no ancestor.
const isPptFrameStoodDown = (state, getters) => {
  return Boolean(
    state.isEnableAddinPowerpoint
    && getters['featureToggles/canUsePptAutoStopQuiz']
    && state.addinPPTActiveView === 'read'
    && state.pptSlideLostFocus,
  )
}

// The EDIT-view counterpart to isPptFrameStoodDown: "this add-in frame's slide is not the one the
// user has selected in the PowerPoint editor." Same job — suppress a background frame's side effects
// — for the session the stand-down flag deliberately cannot cover.
//
// All four terms are load-bearing, and the first two mirror isPptFrameStoodDown's for the same
// reasons:
//   - isEnableAddinPowerpoint — nowhere else runs several frames of one presentation at once.
//   - the flag — off restores the original behaviour exactly, everywhere this is consulted.
//   - addinPPTActiveView === 'edit' — the mirror of the 'read' term above, and it is what keeps the
//     two verdicts from ever both applying. editSelectedSlideIsMine is only written from a
//     DocumentSelectionChanged handler that ignores read-view events, so it goes STALE the moment a
//     show starts; without this term the last thing the editor concluded would follow the frame into
//     the Slide Show and silence it there, over a question the tag poll already owns.
//   - editSelectedSlideIsMine !== true — see below.
//
// FAILS CLOSED, unlike every other unknown-handling decision in this flow. `!== true` means a null
// verdict — "nobody could tell" — silences the frame, where isActiveSlideBoundToThisFrame treats the
// same uncertainty as MINE precisely "so every host that cannot answer behaves exactly as it does
// today". The asymmetry is deliberate and is the reason for the flag: silencing a frame that should
// have played is recoverable by clicking its slide, whereas the read-view case it is modelled on
// would leave a presenter with a dead slide in front of an audience. It does mean a host that never
// services DocumentSelectionChanged has silent slide audio in the editor — which is what the flag
// exists to switch off.
const isPptEditSlideNotSelected = (state, getters) => {
  return Boolean(
    state.isEnableAddinPowerpoint
    && getters['featureToggles/canUsePptEditSlideAudioGate']
    && state.addinPPTActiveView === 'edit'
    && state.pptAddin?.editSelectedSlideIsMine !== true,
  )
}

export default {
  getterEditorModePPT,
  isPptFrameStoodDown,
  isPptEditSlideNotSelected,
}
